import { useLingui } from "@lingui/react/macro";
import { useId, useState } from "react";

import { Button } from "@/shared/ui/button";
import { Spinner } from "@/shared/ui/spinner";

import { useAnswerDrill } from "../../api/useAnswerDrill";
import { useDrillAssets } from "../../api/useDrillAssets";
import { draftAnswer, fullAnswer, isAnswerReady } from "../../model/draftAnswer";
import type { DrillAnswer, DrillItem } from "../../model/Drill";
import { AssetImage } from "./AssetImage";
import { ChoiceOptions } from "./ChoiceOptions";
import { DrillReveal } from "./DrillReveal";
import { DrillSituation } from "./DrillSituation";
import { OrderOptions } from "./OrderOptions";
import { PairsBoard } from "./PairsBoard";
import { PurchaseTimeline } from "./PurchaseTimeline";
import { useDrillQuestion } from "./useDrillQuestion";
import { ValueSlider } from "./ValueSlider";

type DrillViewProps = {
  item: DrillItem;
  isLast: boolean;
  onNext: () => void;
};

// Одна задача серии: ситуация, вопрос, ответ по механике и разбор после ответа.
// Выбор одного варианта и шкала отправляются сразу; остальное — кнопкой «Проверить».
// «Пропустить» отправляет пустой ответ: тема вернётся завтра, как после ошибки.
export const DrillView = ({ item, isLast, onNext }: DrillViewProps) => {
  const { t } = useLingui();
  const headingId = useId();
  const question = useDrillQuestion()(item.spec);
  const assets = useDrillAssets();
  const spec = item.spec;
  const questionItem =
    spec.kind === "buy_minute" && spec.target_item_id !== null
      ? assets.item(spec.target_item_id)
      : null;
  const [draft, setDraft] = useState<DrillAnswer>(() => draftAnswer(spec));
  const { answer, isPending, isError } = useAnswerDrill();
  const attempt = item.attempt;
  const graded = attempt !== null;
  const locked = graded || isPending;
  const shown = attempt === null ? draft : fullAnswer(attempt.answer);
  const submit = (body: DrillAnswer) => {
    if (!locked) {
      answer(item.drill_id, body);
    }
  };
  const inputProps = { spec, answer: shown, graded, locked, onChange: setDraft };
  const instant = spec.mechanic === "choice" || spec.mechanic === "timeline";
  let input;
  switch (spec.mechanic) {
    case "choice":
    case "multi":
      input = <ChoiceOptions {...inputProps} onPick={submit} />;
      break;
    case "order":
      input = <OrderOptions {...inputProps} />;
      break;
    case "time":
      input = <ValueSlider {...inputProps} />;
      break;
    case "pairs":
      input = <PairsBoard {...inputProps} />;
      break;
    case "timeline":
      input = <PurchaseTimeline {...inputProps} onPick={submit} />;
      break;
  }
  return (
    <div className="flex flex-col gap-4">
      <DrillSituation spec={spec} />
      <section aria-labelledby={headingId} className="flex flex-col gap-3">
        <div className="flex items-center gap-3.5">
          {questionItem !== null && (
            <AssetImage asset={questionItem} size={56} className="rounded-[10px]" />
          )}
          <h2 id={headingId} className="text-[22px] leading-tight font-semibold">
            {question.title}
          </h2>
        </div>
        {question.hint !== null && <p className="text-sm text-muted">{question.hint}</p>}
        {input}
        {!graded && !instant && (
          <div className="flex flex-wrap items-center gap-2.5">
            <Button
              size="lg"
              className="min-h-11"
              disabled={!isAnswerReady(spec, draft)}
              loading={isPending}
              onClick={() => {
                submit(draft);
              }}
            >
              {isPending ? t`Checking…` : spec.mechanic === "time" ? t`Answer` : t`Check`}
            </Button>
            {(spec.mechanic === "order" || spec.mechanic === "pairs") && (
              <Button
                variant="link"
                className="min-h-11 text-muted"
                disabled={isPending}
                onClick={() => {
                  setDraft(draftAnswer(spec));
                }}
              >
                {t`Reset`}
              </Button>
            )}
          </div>
        )}
        {isPending && instant && (
          <p role="status" className="inline-flex items-center gap-2 text-sm text-muted">
            <Spinner className="text-accent" />
            {t`Checking…`}
          </p>
        )}
        {isError && (
          <p role="alert" className="text-sm text-bad">
            {t`Could not send the answer. Check the connection and try again.`}
          </p>
        )}
      </section>
      {attempt !== null && <DrillReveal spec={spec} attempt={attempt} />}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {graded ? (
          <Button size="lg" className="min-h-12 px-6" onClick={onNext}>
            {isLast ? t`Series result` : t`Next`}
          </Button>
        ) : (
          <Button
            variant="link"
            className="min-h-11 text-muted"
            disabled={isPending}
            onClick={() => {
              submit({ option_ids: [], value: null, pairs: [] });
            }}
          >
            {t`Skip this task`}
          </Button>
        )}
      </div>
    </div>
  );
};
